import type { ApplicationError } from '@/types/errors';
import { ErrorCategory } from '@/types/errors';

/**
 * Retry strategy configuration
 */
export interface RetryStrategy {
  /**
   * Maximum number of retry attempts
   * @default 3
   */
  maxAttempts: number;

  /**
   * Delay calculation function or static delay in milliseconds
   * @default exponentialBackoff (1s, 2s, 4s, 8s...)
   */
  delayMs: number | ((attempt: number) => number);

  /**
   * Function to determine if error should be retried
   * @default Retries network errors only
   */
  shouldRetry: (error: ApplicationError) => boolean;

  /**
   * Callback invoked before each retry attempt
   */
  onRetry?: (attempt: number, error: ApplicationError) => void;
}

/**
 * Exponential backoff delay calculation
 * Returns: 1s, 2s, 4s, 8s, 16s, 30s (capped at 30s)
 *
 * @param attempt - Zero-based attempt number (0 = first retry)
 * @returns Delay in milliseconds
 */
export function exponentialBackoff(attempt: number): number {
  const baseDelay = 1000; // 1 second
  const maxDelay = 30000; // 30 seconds
  const delay = baseDelay * Math.pow(2, attempt);
  return Math.min(delay, maxDelay);
}

/**
 * Default retry strategy - retries network errors with exponential backoff
 */
export const DEFAULT_RETRY_STRATEGY: RetryStrategy = {
  maxAttempts: 3,
  delayMs: exponentialBackoff,
  shouldRetry: (error: ApplicationError) => {
    // Retry network errors (timeouts, connection issues)
    if (error.category === ErrorCategory.NETWORK) {
      return true;
    }

    // Don't retry authentication/authorization errors
    if (
      error.category === ErrorCategory.AUTHENTICATION ||
      error.category === ErrorCategory.AUTHORIZATION
    ) {
      return false;
    }

    // Don't retry validation errors (client-side issues)
    if (error.category === ErrorCategory.VALIDATION) {
      return false;
    }

    // Don't retry business logic errors (intentional rejections)
    if (error.category === ErrorCategory.BUSINESS_LOGIC) {
      return false;
    }

    // Retry other errors (runtime, unknown)
    return true;
  },
};

/**
 * Manages retry logic with exponential backoff for failed operations
 */
export class RetryManager {
  /**
   * Executes a function with automatic retry logic
   *
   * @param fn - Async function to execute
   * @param strategy - Retry strategy configuration
   * @returns Promise resolving to function result
   * @throws Last error if all retry attempts fail
   *
   * @example
   * ```typescript
   * const result = await RetryManager.withRetry(
   *   () => apiClient.get('/data'),
   *   {
   *     maxAttempts: 3,
   *     delayMs: RetryManager.exponentialBackoff,
   *     shouldRetry: (error) => error.category === ErrorCategory.NETWORK,
   *     onRetry: (attempt, error) => {
   *       console.log(`Retry attempt ${attempt} after error:`, error.message);
   *     }
   *   }
   * );
   * ```
   */
  static async withRetry<T>(
    fn: () => Promise<T>,
    strategy: Partial<RetryStrategy> = {}
  ): Promise<T> {
    const config: RetryStrategy = {
      ...DEFAULT_RETRY_STRATEGY,
      ...strategy,
    };

    let lastError: ApplicationError | undefined;
    let attempt = 0;

    while (attempt < config.maxAttempts) {
      try {
        return await fn();
      } catch (error) {
        lastError = error as ApplicationError;
        attempt++;

        // Check if we should retry this error
        if (!config.shouldRetry(lastError)) {
          throw lastError;
        }

        // Check if we've exhausted all attempts
        if (attempt >= config.maxAttempts) {
          throw lastError;
        }

        // Notify about retry
        config.onRetry?.(attempt, lastError);

        // Calculate delay before next attempt
        const delay =
          typeof config.delayMs === 'function'
            ? config.delayMs(attempt - 1) // attempt - 1 because first retry is attempt 0
            : config.delayMs;

        // Wait before retrying
        await this.sleep(delay);
      }
    }

    // This should never be reached, but TypeScript requires it
    throw lastError!;
  }

  /**
   * Exponential backoff delay calculation (static reference to exported function)
   */
  static exponentialBackoff = exponentialBackoff;

  /**
   * Linear backoff delay calculation
   * Returns: 1s, 2s, 3s, 4s, 5s...
   *
   * @param attempt - Zero-based attempt number
   * @returns Delay in milliseconds
   */
  static linearBackoff(attempt: number): number {
    const baseDelay = 1000; // 1 second
    return baseDelay * (attempt + 1);
  }

  /**
   * Fixed delay between retries
   *
   * @param delayMs - Fixed delay in milliseconds
   * @returns Function that always returns the same delay
   */
  static fixedDelay(delayMs: number): (attempt: number) => number {
    return () => delayMs;
  }

  /**
   * Sleep utility for async waiting
   *
   * @param ms - Milliseconds to sleep
   * @returns Promise that resolves after delay
   */
  private static sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Creates a retry strategy for API calls
   * Retries network errors but not auth/validation errors
   */
  static apiRetryStrategy(maxAttempts = 3): RetryStrategy {
    return {
      maxAttempts,
      delayMs: RetryManager.exponentialBackoff,
      shouldRetry: DEFAULT_RETRY_STRATEGY.shouldRetry,
    };
  }

  /**
   * Creates a retry strategy for mutations
   * More conservative - fewer retries, longer delays
   */
  static mutationRetryStrategy(): RetryStrategy {
    return {
      maxAttempts: 2,
      delayMs: RetryManager.exponentialBackoff,
      shouldRetry: (error: ApplicationError) => {
        // Only retry network timeouts for mutations
        return (
          error.category === ErrorCategory.NETWORK &&
          error.code === 'NETWORK_TIMEOUT'
        );
      },
    };
  }
}
